import {
  reactQueryDelete,
  reactQueryGet,
  reactQueryPatch,
  reactQueryPost,
} from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";

export interface CircleAiProject {
  archived: boolean;
  archived_at: string | null;
  conversations_count: number;
  created_at: string;
  description: string | null;
  id: number;
  instructions: string | null;
  name: string;
  updated_at: string;
  uuid: string;
}

export interface CircleAiProjectsListResponse {
  pagination: {
    page: number;
    per_page: number;
    total_count: number;
  };
  records: CircleAiProject[];
}

export type CircleAiProjectFileProcessingStatus =
  | "pending"
  | "processing"
  | "indexed"
  | "failed";

export interface CircleAiProjectFile {
  attachment_id: number | null;
  byte_size: number | null;
  content_type: string | null;
  created_at: string;
  download_url: string | null;
  filename: string | null;
  id: number;
  processing_error: string | null;
  processing_status: CircleAiProjectFileProcessingStatus;
}

export interface CircleAiProjectFilesListResponse {
  pagination: {
    page: number;
    per_page: number;
    total_count: number;
  };
  records: CircleAiProjectFile[];
}

interface CircleAiProjectsListParams {
  params?: {
    page?: number;
    per_page?: number;
    query?: string;
  };
}

export interface CircleAiProjectAttributes {
  description: string;
  instructions?: string | null;
  name: string;
}

export type CircleAiProjectUpdateAttributes =
  Partial<CircleAiProjectAttributes>;

interface CircleAiProjectPayload {
  project: CircleAiProjectAttributes;
}

interface CircleAiProjectUpdatePayload {
  project: CircleAiProjectUpdateAttributes;
}

const list = ({ params = {} }: CircleAiProjectsListParams = {}) =>
  reactQueryGet<CircleAiProjectsListResponse>(
    internalApi.circleAiProjects.list({ params }),
  );

const archivedList = ({ params = {} }: CircleAiProjectsListParams = {}) =>
  reactQueryGet<CircleAiProjectsListResponse>(
    internalApi.circleAiProjects.archived({ params }),
  );

const show = ({ uuid }: { uuid: string }) =>
  reactQueryGet<CircleAiProject>(internalApi.circleAiProjects.show({ uuid }));

const create = ({ project }: CircleAiProjectPayload) =>
  reactQueryPost<CircleAiProject, CircleAiProjectPayload>(
    internalApi.circleAiProjects.create(),
    { project },
  );

const update = ({
  uuid,
  project,
}: {
  project: CircleAiProjectUpdateAttributes;
  uuid: string;
}) =>
  reactQueryPatch<CircleAiProject, CircleAiProjectUpdatePayload>(
    internalApi.circleAiProjects.update({ uuid }),
    { project },
  );

const archive = ({ uuid }: { uuid: string }) =>
  reactQueryPost<void, never>(internalApi.circleAiProjects.archive({ uuid }));

const unarchive = ({ uuid }: { uuid: string }) =>
  reactQueryPost<void, never>(internalApi.circleAiProjects.unarchive({ uuid }));

const destroy = ({ uuid }: { uuid: string }) =>
  reactQueryDelete<void, never>(internalApi.circleAiProjects.destroy({ uuid }));

const filesList = ({
  uuid,
  params = {},
}: {
  params?: { per_page?: number };
  uuid: string;
}) =>
  reactQueryGet<CircleAiProjectFilesListResponse>(
    internalApi.circleAiProjects.files.index({ uuid, params }),
  );

const fileCreate = ({ uuid, signedId }: { signedId: string; uuid: string }) =>
  reactQueryPost<CircleAiProjectFile, { file: { signed_id: string } }>(
    internalApi.circleAiProjects.files.index({ uuid }),
    { file: { signed_id: signedId } },
  );

const fileDestroy = ({ uuid, id }: { id: number; uuid: string }) =>
  reactQueryDelete<void, never>(
    internalApi.circleAiProjects.files.destroy({ uuid, id }),
  );

export const circleAiProjectsApi = {
  list,
  archivedList,
  show,
  create,
  update,
  archive,
  unarchive,
  destroy,
  filesList,
  fileCreate,
  fileDestroy,
};
